import React from "react";
import { useFormik } from "formik";
import { io, Socket } from "socket.io-client";
import { toast } from "react-toastify";

import { Button, Input, Select, Sidebar, LeadSenderAvatar } from "../../ui";
import { connectLinkedInCredentialsSchema } from "../../../validators/linkedInValidators";
import {
  disconnectLinkedinSender,
  fetchUserOwnersWithWorkspaces,
} from "../../../utils";
import axios from "../../../utils/axios";
import { SOCKET_URL, SOCKET_IO_CLIENT_OPTIONS } from "../../../constants";
import { sanitizeLinkedinProfilePictureUrl } from "../../../utils/linkedinProfilePicture";
import MultipleAccountsHelpGuide from "./MultipleAccountsHelpGuide";

type ConnectLinkedinCredentialsSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  mode?: "manual" | "extension";
  extensionId?: string;
  reconnectOwnerId?: string;
  onConnected?: (owner: Record<string, any>) => void;
  onDisconnected?: (ownerId: string) => void;
};

const LINKEDIN_LOGOUT_URL = "https://www.linkedin.com/m/logout/";
const postWebConnectionAudit = (payload: Record<string, unknown>) => {
  console.warn("[LS_CONNECTION_AUDIT]", payload);
  axios.post("/owner/logConnectionAudit", payload).catch(() => {
    // best-effort audit log
  });
};
/** Align with backend LINKEDIN_CREDENTIALS_SIGNIN_WAIT_MS (default 300s) plus buffer. */
/** Backend default wait is 300s; stay ahead so Microsoft SSO is not cut off early. */
const MANUAL_CONNECT_TIMEOUT_MS = 390000;

function formatManualSocketConnectError(err: unknown, socketUrl: string): string {
  const anyErr = err as {
    message?: string;
    description?: string;
    data?: { message?: string };
    type?: string;
    context?: { status?: number };
  };
  const msg = String(
    anyErr?.message || anyErr?.description || anyErr?.data?.message || ""
  ).trim();
  const lower = msg.toLowerCase();
  const status = anyErr?.context?.status;

  if (!msg) {
    return `Could not open a live connection to the LinkedIn connect service at ${socketUrl}. Confirm this URL matches your running API (same host/port as the app) and that the backend is up.`;
  }

  if (lower.includes("xhr poll error") || lower === "xhr poll error") {
    return "The browser could not reach the connect service over HTTP polling. Check VPN/firewall, mixed content (HTTPS site calling HTTP socket), and CORS or reverse-proxy rules for Socket.IO.";
  }

  if (
    lower.includes("websocket error") ||
    lower.includes("websocket is closed before the connection is established") ||
    lower.includes("ws connection error")
  ) {
    return "WebSocket upgrade to the connect service failed. Corporate proxies and some load balancers block WebSockets; ask ops to allow WS on the API path or terminate TLS correctly.";
  }

  if (lower.includes("timeout") || anyErr?.type === "TransportError") {
    return "Connecting to the LinkedIn connect service timed out. Verify the API is reachable from your network and not overloaded.";
  }

  if (status === 401 || lower.includes("unauthorized")) {
    return "The connect service rejected the connection (unauthorized). Refresh the app, sign in again, and retry.";
  }

  if (status === 403 || lower.includes("forbidden")) {
    return "The connect service rejected the connection (forbidden). Check allowed origins and API gateway rules.";
  }

  if (lower.includes("certificate") || lower.includes("ssl") || lower.includes("cert")) {
    return "TLS/certificate error while connecting to the connect service. Fix HTTPS certificates or use the correct API URL.";
  }

  return `Could not reach the LinkedIn connect service: ${msg}`;
}

function formatManualSocketDisconnectReason(reason: string): string {
  const r = String(reason || "");
  switch (r) {
    case "io server disconnect":
      return "The connect service closed your session (often a server restart or crash during login). Retry; if it repeats, check backend logs.";
    case "transport close":
      return "The network closed the connection to the connect service mid-login. Retry on a stable connection.";
    case "transport error":
      return "Transport error while talking to the connect service. Check proxies, mixed HTTP/HTTPS, and SSL.";
    case "ping timeout":
      return "The connect service stopped responding (ping timeout). The server may be overloaded or the route blocked.";
    case "parse error":
      return "Invalid Socket.IO response from the server. Ensure frontend and backend use compatible socket.io versions.";
    default:
      return r
        ? `Disconnected from the connect service (${r}). Retry connect.`
        : "Disconnected from the connect service. Retry connect.";
  }
}

function toUserFriendlyConnectMessage(rawMessage: string): string {
  const raw = String(rawMessage || "")
    .replace(/^axioserror:\s*/i, "")
    .replace(/^error:\s*/i, "")
    .trim();
  const lower = raw.toLowerCase();
  const reconnectRetryMessage =
    "LinkedIn sign-in could not be completed just now. Please click Connect Account to try again. If this keeps happening, use Connect with Chrome extension.";
  if (!raw) return "We could not complete LinkedIn connection. Please try again.";

  if (
    lower.includes("couldn't finish connecting this linkedin account") ||
    lower.includes("wait a few minutes, then click connect account") ||
    lower.includes("if it keeps failing, use connect with chrome extension") ||
    lower.includes("still waiting for your email verification code")
  ) {
    return String(raw || "").trim();
  }
  if (
    lower.includes("no li_at") ||
    lower.includes("did not establish a session") ||
    lower.includes("missing_cookies") ||
    lower.includes("did not finish sign-in") ||
    lower.includes("still on the login page")
  ) {
    return "We couldn't finish connecting this LinkedIn account. Wait a few minutes, then click Connect Account to try again. If it keeps failing, use Connect with Chrome extension.";
  }

  const statusCodeMatch = lower.match(/status code\s+(\d{3})/);
  const statusCode = Number(statusCodeMatch?.[1] || 0);
  if (statusCode === 400) {
    return "LinkedIn connect request is invalid. Please verify details and try again.";
  }
  if (statusCode === 401) {
    return "Your session expired. Please refresh, sign in again, and retry.";
  }
  if (statusCode === 403) {
    return "LinkedIn connect was blocked for this request. Please retry after a short wait.";
  }
  if (statusCode >= 500) {
    return "LinkedIn connect service is temporarily unavailable. Please retry in a moment.";
  }

  if (
    lower.includes("wrong email or password") ||
    lower.includes("incorrect linkedin email or password") ||
    lower.includes("incorrect email or password") ||
    lower.includes("invalid email or password")
  ) {
    return "Incorrect LinkedIn email or password. Please update your credentials and try again.";
  }
  if (lower.includes("missing user context")) {
    return "Your session expired. Please refresh, sign in again, and retry.";
  }
  if (
    lower.includes("unable to reach linkedin with this account") ||
    lower.includes("temporarily restricted") ||
    lower.includes("automation detected") ||
    lower.includes("linkedin_account_restricted") ||
    lower.includes("linkedin_account_unreachable")
  ) {
    return "We are currently unable to reach LinkedIn with this account. Try logging into this account first in a browser; if everything looks fine, try again here.";
  }
  if (
    lower.includes("headless automated sign-in cannot") ||
    lower.includes("security challenge (captcha") ||
    lower.includes("security challenge (for example") ||
    lower.includes("re-connect using connect with chrome extension") ||
    lower.includes("re-connect this account")
  ) {
    return String(raw || "").trim();
  }
  if (
    lower.includes("microsoft sign-in did not complete in time") ||
    lower.includes("microsoft sign-in did not finish in time") ||
    lower.includes("did not reach the verification step") ||
    lower.includes("microsoft is still waiting for your sign-in code") ||
    lower.includes("microsoft sign-in timed out before your verification code") ||
    lower.includes("the browser session has ended") ||
    lower.includes("microsoft sign-in did not finish") ||
    lower.includes("microsoft could not verify this email address") ||
    lower.includes("microsoft sign-in could not be completed") ||
    lower.includes("request a fresh verification code from microsoft") ||
    lower.includes("request a new code from microsoft") ||
    lower.includes("rejected or expired") ||
    lower.includes("that verification code was rejected") ||
    lower.includes("that verification code was not accepted") ||
    lower.includes("never send it to microsoft") ||
    lower.includes("microsoft account password") ||
    lower.includes("that microsoft password was not accepted") ||
    lower.includes("stay-signed-in prompt") ||
    lower.includes("did not finish after the verification code")
  ) {
    return String(raw || "").trim();
  }
  if (
    lower.includes("microsoft or google sign-in") ||
    lower.includes("sent sign-in to microsoft") ||
    lower.includes("login.live.com") ||
    lower.includes("external sso")
  ) {
    return "Microsoft sign-in needs a verification code. Check your email (including spam), enter the latest code below, and wait.";
  }
  if (lower.includes("approve the sign-in in the linkedin app")) {
    return "LinkedIn asked for verification. Approve the sign-in in the LinkedIn app if prompted, then wait while we finish connecting.";
  }
  if (
    lower.includes("captcha") ||
    lower.includes("recaptcha") ||
    lower.includes("hcaptcha") ||
    lower.includes("funcaptcha")
  ) {
    return "LinkedIn is showing a security challenge (captcha). Automated sign-in cannot complete this. Re-connect using Connect with Chrome extension.";
  }
  if (lower.includes("app verification")) {
    return "LinkedIn needs app verification. Approve the sign-in in LinkedIn app, then continue.";
  }
  if (lower.includes("verification code") && lower.includes("linkedin")) {
    return "LinkedIn sent a verification code. Enter the code to continue.";
  }
  if (lower.includes("connect service") || lower.includes("socket")) {
    return "Could not reach the LinkedIn connect service. Please check your network or try again in a moment.";
  }
  if (lower.includes("network error") || lower.includes("failed to fetch")) {
    return "Network issue while connecting to LinkedIn. Please check your connection and retry.";
  }
  if (
    lower.includes("err_tunnel_connection_failed") ||
    lower.includes("tunnel_connection_failed") ||
    lower.includes("proxy_tunnel_failed") ||
    lower.includes("could not be reached through the residential proxy") ||
    lower.includes("connection tunnel failed")
  ) {
    return "LinkedIn could not be reached through the residential proxy. Your internet is fine—click Connect Account to try again. If it keeps failing, use Connect with Chrome extension.";
  }
  if (
    lower.includes("proxy rejected") ||
    lower.includes("proxy_auth_failed") ||
    lower.includes("iproyal proxy")
  ) {
    return "The residential proxy could not sign in to LinkedIn. Click Connect Account to try again, or use Connect with Chrome extension.";
  }
  if (
    lower.includes("sign-in button was not found") ||
    lower.includes("sign in button was not found") ||
    lower.includes("verification submit button was not found") ||
    lower.includes("not clickable") ||
    lower.includes("verification code field was not found") ||
    lower.includes("still waiting for your email verification code") ||
    lower.includes("could not submit the verification code") ||
    lower.includes("login form was not found") ||
    lower.includes("did not render expected fields") ||
    lower.includes("timeout waiting for visible password field") ||
    lower.includes("form_automation_failed")
  ) {
    return reconnectRetryMessage;
  }
  if (lower.includes("page.goto") && lower.includes("net::err")) {
    return "LinkedIn login could not start in the browser. Click Connect Account to try again. If this repeats, use Connect with Chrome extension.";
  }
  if (lower.includes("timed out") || lower.includes("timeout")) {
    return "LinkedIn is taking longer than expected. Please retry in a few moments.";
  }
  return raw;
}

function getManualProgressMessage(stage: string): string {
  switch (stage) {
    case "connecting":
      return "Connecting to LinkedIn...";
    case "verifying_session":
      return "Verifying LinkedIn session...";
    case "pin_required":
      return "Enter LinkedIn verification code below";
    case "microsoft_pin":
      return "Enter Microsoft verification code...";
    case "microsoft_signin":
      return "LinkedIn is verifying sign-in…";
    case "microsoft_password":
      return "Microsoft password required (optional)";
    case "app_challenge":
      return "Waiting for app verification...";
    default:
      return "Working...";
  }
}

/** True while credentials connect must not be restarted (verification in progress). */
function isManualConnectLocked(isConnecting: boolean, stage: string): boolean {
  return (
    isConnecting ||
    stage === "app_challenge" ||
    stage === "verifying_session" ||
    stage === "connecting" ||
    stage === "pin_required" ||
    stage === "microsoft_pin" ||
    stage === "microsoft_signin" ||
    stage === "microsoft_password"
  );
}

/**
 * HTML fieldset[disabled] blocks ALL inputs inside it — never disable the fieldset
 * while the user must enter a verification code.
 */
function isCredentialsFieldsetLocked(isConnecting: boolean, stage: string): boolean {
  if (
    stage === "microsoft_pin" ||
    stage === "pin_required" ||
    stage === "microsoft_password"
  ) {
    return false;
  }
  return isManualConnectLocked(isConnecting, stage);
}

/** Lock email/password/selects during connect; pin field uses its own rules. */
function areCredentialInputsLocked(isConnecting: boolean, stage: string): boolean {
  return (
    isManualConnectLocked(isConnecting, stage) ||
    stage === "microsoft_pin" ||
    stage === "pin_required" ||
    stage === "microsoft_signin" ||
    stage === "microsoft_password"
  );
}

function isManualConnectNotice(stage: string): boolean {
  return (
    stage === "app_challenge" ||
    stage === "verifying_session" ||
    stage === "pin_required" ||
    stage === "microsoft_pin" ||
    stage === "microsoft_signin" ||
    stage === "microsoft_password"
  );
}

/** Only when the backend detected an actual code-entry step on Microsoft's page. */
function showManualPinUi(stage: string): boolean {
  return stage === "pin_required" || stage === "microsoft_pin";
}

function showMicrosoftPasswordUi(stage: string): boolean {
  return stage === "microsoft_password";
}

function getManualPinHint(stage: string): string {
  if (stage === "microsoft_pin") {
    return "Microsoft is asking for a one-time sign-in code. Check the inbox for this account (and spam), then enter the newest code below.";
  }
  return "LinkedIn sent a verification code to your email. Enter the code below to finish signing in.";
}

function getMicrosoftPasswordHint(): string {
  return "LinkedIn is completing an extra sign-in check through Microsoft. Your LinkedIn password is only used on LinkedIn.com — we never send it to Microsoft. If Microsoft is asking for a password, enter your Microsoft account password for this email below. If Microsoft sends a code instead, wait for the code field above.";
}

function extractApiErrorMessage(error: any, fallback: string): string {
  return String(
    error?.response?.data?.msg ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
  );
}

export default function ConnectLinkedinCredentialsSidebar({
  isOpen,
  onClose,
  onBack,
  mode = "manual",
  extensionId = "",
  reconnectOwnerId = "",
  onConnected,
  onDisconnected,
}: ConnectLinkedinCredentialsSidebarProps) {
  const isCredentialsDisabled = false;
  const resolveExtensionId = React.useCallback(() => {
    const marker = document.getElementById("SCR_Extension_Installed");
    const markerExtensionId = marker?.getAttribute("data-extension-id") || "";
    const maybeWindow = window as Window & { __INNLEAD_EXTENSION_ID__?: string };
    const webStoreUrl = process.env.REACT_APP_CHROME_WEBSTORE_URL || "";
    const webStoreMatch = webStoreUrl.match(/\/detail\/[^/]+\/([a-z]{32})(?:[/?#]|$)/i);
    const extensionIdFromStoreUrl = webStoreMatch?.[1] || "";
    return (
      extensionId ||
      markerExtensionId ||
      maybeWindow.__INNLEAD_EXTENSION_ID__ ||
      process.env.REACT_APP_CHROME_EXTENSION_ID ||
      extensionIdFromStoreUrl ||
      ""
    );
  }, [extensionId]);

  const [showOwnProxy, setShowOwnProxy] = React.useState(false);
  const [showConfirmStep, setShowConfirmStep] = React.useState(false);
  const [isExtensionProfileLoading, setIsExtensionProfileLoading] = React.useState(false);
  const [isExtensionLinkedinLoggedOut, setIsExtensionLinkedinLoggedOut] =
    React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [isVerifyingProxy, setIsVerifyingProxy] = React.useState(false);
  const [connectError, setConnectError] = React.useState("");
  const [isAlreadyConnected, setIsAlreadyConnected] = React.useState(false);
  const [connectedOwnerId, setConnectedOwnerId] = React.useState("");
  const [isDisconnectingConnected, setIsDisconnectingConnected] =
    React.useState(false);
  const [manualStage, setManualStage] = React.useState<
    | "idle"
    | "connecting"
    | "pin_required"
    | "microsoft_pin"
    | "microsoft_signin"
    | "microsoft_password"
    | "app_challenge"
    | "verifying_session"
  >("idle");
  const manualStageRef = React.useRef(manualStage);
  /** Set synchronously so socket handlers see the latest challenge before React re-renders. */
  const manualChallengeKindRef = React.useRef<string | null>(null);
  const manualConnectCompletedRef = React.useRef(false);
  const manualFinalizingRef = React.useRef(false);
  const manualTerminalConnectErrorRef = React.useRef(false);
  const manualConnectTimeoutRef = React.useRef<number | null>(null);
  const manualBaselineOwnerCountRef = React.useRef<number>(-1);
  const extensionProfileRequestedRef = React.useRef(false);
  const [manualPin, setManualPin] = React.useState("");
  const [manualMicrosoftPassword, setManualMicrosoftPassword] = React.useState("");
  const [isProxyVerified, setIsProxyVerified] = React.useState(false);
  const manualSocketRef = React.useRef<Socket | null>(null);
  const manualPinSectionRef = React.useRef<HTMLDivElement | null>(null);
  const manualMicrosoftPasswordSectionRef = React.useRef<HTMLDivElement | null>(null);
  const manualPinSubmitInFlightRef = React.useRef(false);
  const manualPinSubmittedAtRef = React.useRef(0);
  /** True only while user explicitly started credentials connect (prevents reconnect replay). */
  const manualConnectRequestedRef = React.useRef(false);
  const [linkedInProfile, setLinkedInProfile] = React.useState<{
    name: string;
    profilePicture: string;
    country: string;
    entityUrn: string;
    publicIdentifier: string;
  }>({
    name: "",
    profilePicture: "",
    country: "",
    entityUrn: "",
    publicIdentifier: "",
  });
  const countryOptions = React.useMemo(() => {
    const regionCodes = [
      "AF","AL","DZ","AS","AD","AO","AI","AQ","AG","AR","AM","AW","AU","AT","AZ","BS","BH","BD","BB","BY","BE","BZ","BJ","BM","BT","BO","BQ","BA","BW","BV","BR","IO","BN","BG","BF","BI","CV","KH","CM","CA","KY","CF","TD","CL","CN","CX","CC","CO","KM","CG","CD","CK","CR","CI","HR","CU","CW","CY","CZ","DK","DJ","DM","DO","EC","EG","SV","GQ","ER","EE","SZ","ET","FK","FO","FJ","FI","FR","GF","PF","TF","GA","GM","GE","DE","GH","GI","GR","GL","GD","GP","GU","GT","GG","GN","GW","GY","HT","HM","VA","HN","HK","HU","IS","IN","ID","IR","IQ","IE","IM","IL","IT","JM","JP","JE","JO","KZ","KE","KI","KP","KR","KW","KG","LA","LV","LB","LS","LR","LY","LI","LT","LU","MO","MG","MW","MY","MV","ML","MT","MH","MQ","MR","MU","YT","MX","FM","MD","MC","MN","ME","MS","MA","MZ","MM","NA","NR","NP","NL","NC","NZ","NI","NE","NG","NU","NF","MK","MP","NO","OM","PK","PW","PS","PA","PG","PY","PE","PH","PN","PL","PT","PR","QA","RE","RO","RU","RW","BL","SH","KN","LC","MF","PM","VC","WS","SM","ST","SA","SN","RS","SC","SL","SG","SX","SK","SI","SB","SO","ZA","GS","SS","ES","LK","SD","SR","SJ","SE","CH","SY","TW","TJ","TZ","TH","TL","TG","TK","TO","TT","TN","TR","TM","TC","TV","UG","UA","AE","GB","US","UM","UY","UZ","VU","VE","VN","VG","VI","WF","EH","YE","ZM","ZW",
    ];
    try {
      if (
        typeof Intl === "undefined" ||
        typeof Intl.DisplayNames === "undefined"
      ) {
        return [
          { value: "Pakistan", label: "Pakistan" },
          { value: "United States", label: "United States" },
          { value: "United Kingdom", label: "United Kingdom" },
        ];
      }

      const display = new Intl.DisplayNames(["en"], { type: "region" });
      return regionCodes
        .map((code) => {
          const name = display.of(code);
          return { value: name || "", label: name || "" };
        })
        .filter((item) => item.value)
        .sort((a, b) => a.label.localeCompare(b.label));
    } catch {
      return [
        { value: "Pakistan", label: "Pakistan" },
        { value: "United States", label: "United States" },
        { value: "United Kingdom", label: "United Kingdom" },
      ];
    }
  }, []);
  const normalizeCountryText = React.useCallback((value: string) => {
    return (value || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const resolveCountryFromExtensionProfile = React.useCallback(
    (profile?: {
      country?: string;
      geoCountryName?: string;
      location?: string;
      geoLocationName?: string;
    }) => {
      if (!profile) return "";

      const aliases: Record<string, string> = {
        usa: "United States",
        us: "United States",
        "united states of america": "United States",
        uk: "United Kingdom",
        uae: "United Arab Emirates",
      };

      const optionsIndex = countryOptions.map((option) => ({
        raw: option.value,
        normalized: normalizeCountryText(option.value),
      }));
      const exactFromValue = (value?: string) => {
        const chunks = (value || "")
          .split(/[,()/\-]+/)
          .map((part) => part.trim())
          .filter(Boolean);
        for (const chunk of chunks) {
          const normalizedChunk = normalizeCountryText(chunk);
          const aliased = aliases[normalizedChunk] || chunk;
          const exact = optionsIndex.find(
            (entry) => entry.normalized === normalizeCountryText(aliased)
          );
          if (exact) return exact.raw;
        }
        return "";
      };

      // Hard priority: use geo location text first when it contains a country.
      const fromGeoLocation = exactFromValue(profile.geoLocationName || profile.location);
      if (fromGeoLocation) return fromGeoLocation;

      const rawCandidates = [
        // Prefer geo-location text when available.
        profile.geoLocationName,
        profile.location,
        profile.geoCountryName,
        profile.country,
      ].filter(Boolean) as string[];

      const normalizedCandidates = rawCandidates
        .flatMap((candidate) =>
          candidate
            .split(/[,()/\-]+/)
            .map((part) => part.trim())
            .filter(Boolean)
        )
        .map((part) => {
          const normalized = normalizeCountryText(part);
          return aliases[normalized] || part;
        });

      for (const candidate of normalizedCandidates) {
        const normalizedCandidate = normalizeCountryText(candidate);
        const exact = optionsIndex.find((entry) => entry.normalized === normalizedCandidate);
        if (exact) return exact.raw;
      }

      for (const candidate of normalizedCandidates) {
        const normalizedCandidate = normalizeCountryText(candidate);
        const fuzzy = optionsIndex.find(
          (entry) =>
            normalizedCandidate.includes(entry.normalized) ||
            entry.normalized.includes(normalizedCandidate)
        );
        if (fuzzy) return fuzzy.raw;
      }

      return "";
    },
    [countryOptions, normalizeCountryText]
  );

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      inboxPrivacy: "",
      country: "",
      proxyHost: "",
      proxyPort: "",
      proxyUsername: "",
      proxyPassword: "",
    },
    validationSchema: connectLinkedInCredentialsSchema,
    onSubmit: async () => {
      if (mode === "manual") {
        if (!formik.values.inboxPrivacy || !formik.values.country) {
          setConnectError("Please select inbox privacy and country.");
          return;
        }
        if (!formik.values.email || !formik.values.password) {
          setConnectError("Please provide LinkedIn email and password.");
          return;
        }
        connectWithManualCredentials();
        return;
      }
      setShowConfirmStep(true);
    },
  });

  React.useEffect(() => {
    manualStageRef.current = manualStage;
  }, [manualStage]);

  React.useEffect(() => {
    if (mode !== "manual" || !showManualPinUi(manualStage)) return;
    const frame = window.requestAnimationFrame(() => {
      manualPinSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [mode, manualStage]);

  React.useEffect(() => {
    if (mode !== "manual" || !showMicrosoftPasswordUi(manualStage)) return;
    const frame = window.requestAnimationFrame(() => {
      manualMicrosoftPasswordSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [mode, manualStage]);

  React.useEffect(() => {
    if (!isOpen) {
      extensionProfileRequestedRef.current = false;
      manualConnectRequestedRef.current = false;
      if (manualSocketRef.current) {
        manualSocketRef.current.disconnect();
        manualSocketRef.current = null;
      }
      if (manualConnectTimeoutRef.current) {
        window.clearTimeout(manualConnectTimeoutRef.current);
        manualConnectTimeoutRef.current = null;
      }
      manualFinalizingRef.current = false;
      setShowOwnProxy(false);
      setShowConfirmStep(false);
      setIsExtensionProfileLoading(false);
      setIsExtensionLinkedinLoggedOut(false);
      setLinkedInProfile({
        name: "",
        profilePicture: "",
        country: "",
        entityUrn: "",
        publicIdentifier: "",
      });
      setIsConnecting(false);
      setConnectError("");
      setIsAlreadyConnected(false);
      setManualStage("idle");
      setManualPin("");
      setIsProxyVerified(false);
      formik.resetForm();
      return;
    }

    setShowOwnProxy(false);
    setIsProxyVerified(false);
    setShowConfirmStep(mode === "extension");
    setIsExtensionProfileLoading(mode === "extension");
    setIsExtensionLinkedinLoggedOut(false);
  }, [isOpen, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isOpen || mode !== "extension" || !extensionId) return;
    if (extensionProfileRequestedRef.current) return;
    extensionProfileRequestedRef.current = true;

    const browserChrome = (window as Window & { chrome?: any }).chrome;
    const chromeRuntime = browserChrome?.runtime;
    if (!chromeRuntime?.sendMessage) {
      setIsExtensionProfileLoading(false);
      setIsExtensionLinkedinLoggedOut(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsExtensionProfileLoading(false);
      setIsExtensionLinkedinLoggedOut(true);
    }, 6000);

    try {
      // Push current web owner/workspace context into extension storage so
      // extension-side validation has the same scope as web.
      try {
        const ownerRaw = localStorage.getItem("owner");
        const workspaceRaw = localStorage.getItem("currentWorkSpace");
        const ownerContext = ownerRaw ? JSON.parse(ownerRaw) : null;
        const reconnectOwnerContext =
          reconnectOwnerId && ownerContext
            ? {
                ...ownerContext,
                _id: reconnectOwnerId,
                owner: {
                  ...(ownerContext?.owner || {}),
                  _id: reconnectOwnerId,
                },
              }
            : ownerContext;
        const workspaceContext = workspaceRaw ? JSON.parse(workspaceRaw) : null;
        chromeRuntime.sendMessage(
          extensionId,
          {
            cmd: "syncOwnerContext",
            data: {
              owner: reconnectOwnerContext,
              workspace: workspaceContext,
            },
          },
          () => {
            // best-effort context sync; continue flow regardless.
          }
        );
      } catch {
        // ignore context sync parse/runtime errors
      }

      chromeRuntime.sendMessage(
        extensionId,
        { cmd: "getLinkedinProfile" },
        async (res: {
          status?: boolean;
          profile?: {
            name?: string;
            firstName?: string;
            lastName?: string;
            profilePicture?: string;
            picture?: string;
            avatar?: string;
            entityUrn?: string;
            publicIdentifier?: string;
            country?: string;
            geoCountryName?: string;
            location?: string;
            geoLocationName?: string;
          };
        }) => {
          const chromeLastError = browserChrome?.runtime?.lastError;
          if (chromeLastError || !res?.status || !res?.profile) {
            window.clearTimeout(timeoutId);
            setIsExtensionProfileLoading(false);
            setIsExtensionLinkedinLoggedOut(true);
            return;
          }

          const profileName =
            res.profile.name ||
            [res.profile.firstName || "", res.profile.lastName || ""].join(" ").trim() ||
            "";
          const profilePicture = sanitizeLinkedinProfilePictureUrl(
            res.profile.profilePicture || res.profile.picture || res.profile.avatar || ""
          );
          const rawCountry = resolveCountryFromExtensionProfile({
            country: res.profile.country,
            geoCountryName: res.profile.geoCountryName,
            location: res.profile.location,
            geoLocationName: res.profile.geoLocationName,
          });
          const profileCountry = rawCountry;
          const profileEntityUrn = String(res.profile.entityUrn || "").trim();
          const profilePublicIdentifier = String(
            res.profile.publicIdentifier || ""
          ).trim();

          try {
            const accountsRes = await fetchUserOwnersWithWorkspaces(1, 50, "");
            const accounts: Array<Record<string, any>> = Array.isArray(
              accountsRes?.accounts
            )
              ? accountsRes.accounts
              : [];

            let extensionSessionValid = false;
            try {
              const validationRes = await new Promise<{
                isValid?: boolean;
                status?: boolean;
              } | null>((resolve) => {
                chromeRuntime.sendMessage(
                  extensionId,
                  { cmd: "validateOwnerSession" },
                  (resValidation: { isValid?: boolean } | undefined) => {
                    const err = browserChrome?.runtime?.lastError;
                    if (err || !resValidation) return resolve(null);
                    resolve(resValidation);
                  }
                );
              });
              extensionSessionValid = Boolean(validationRes?.isValid);
            } catch {
              extensionSessionValid = false;
            }

            if (accounts.length > 0) {
              const normalizePublicIdentifier = (value: unknown) => {
                const raw = String(value || "").trim();
                if (!raw) return "";
                const match = raw.match(/\/in\/([^/?#]+)/i);
                const id = match?.[1] ? match[1] : raw;
                return String(id).replace(/\/+$/g, "").trim().toLowerCase();
              };

              const normalizeEntityUrn = (value: unknown) => {
                return String(value || "")
                  .trim()
                  .replace(/\/+$/g, "")
                  .toLowerCase();
              };

              const normalizedProfileEntityUrn = normalizeEntityUrn(
                profileEntityUrn
              );
              const normalizedProfilePublicIdentifier =
                normalizePublicIdentifier(profilePublicIdentifier);

              const matchedConnectedAccount = accounts.find((acc) => {
                const accEntityUrn = normalizeEntityUrn(acc?.ownerEntityUrn);
                const accPublicIdentifier = normalizePublicIdentifier(
                  acc?.publicIdentifier
                );
                const accIsDisconnected = Boolean(acc?.isDisconnected);
                const accHasToken = Boolean(acc?.hasLinkedinToken);

                const matchesEntity =
                  normalizedProfileEntityUrn &&
                  accEntityUrn === normalizedProfileEntityUrn;
                const matchesPublic =
                  normalizedProfilePublicIdentifier &&
                  accPublicIdentifier === normalizedProfilePublicIdentifier;

                // Reconnect flow: a matching disconnected sender should NOT be treated as
                // "already connected". Only active + token-present senders qualify.
                return (matchesEntity || matchesPublic) && !accIsDisconnected && accHasToken;
              });
              const matchedProfileAccount = accounts.find((acc) => {
                const accEntityUrn = normalizeEntityUrn(acc?.ownerEntityUrn);
                const accPublicIdentifier = normalizePublicIdentifier(
                  acc?.publicIdentifier
                );
                const matchesEntity =
                  normalizedProfileEntityUrn &&
                  accEntityUrn === normalizedProfileEntityUrn;
                const matchesPublic =
                  normalizedProfilePublicIdentifier &&
                  accPublicIdentifier === normalizedProfilePublicIdentifier;
                return matchesEntity || matchesPublic;
              });
              const alreadyConnectedByBackend = Boolean(matchedConnectedAccount);

              // Backend list is authoritative for whether the current
              // LinkedIn profile is already connected.
              const finalAlreadyConnected = alreadyConnectedByBackend;

              if (
                extensionSessionValid &&
                matchedProfileAccount &&
                Boolean(matchedProfileAccount?.isDisconnected)
              ) {
                postWebConnectionAudit({
                  event: "web_sidebar_extension_backend_mismatch",
                  source: "web_connect_sidebar",
                  ownerId:
                    matchedProfileAccount?._id ||
                    matchedProfileAccount?.ownerId ||
                    "",
                  extensionShowsConnected: true,
                  backendIsDisconnected: true,
                  backendHasLinkedinToken: Boolean(
                    matchedProfileAccount?.hasLinkedinToken
                  ),
                  extensionSessionValid,
                  linkedInPublicIdentifier: profilePublicIdentifier,
                  linkedInEntityUrn: profileEntityUrn,
                  details: {
                    finalAlreadyConnected,
                    extensionSessionValid,
                  },
                });
              } else {
                postWebConnectionAudit({
                  event: finalAlreadyConnected
                    ? "web_sidebar_connection_status_connected"
                    : "web_sidebar_connection_status_not_connected",
                  source: "web_connect_sidebar",
                  ownerId:
                    matchedProfileAccount?._id ||
                    matchedProfileAccount?.ownerId ||
                    matchedConnectedAccount?._id ||
                    matchedConnectedAccount?.ownerId ||
                    "",
                  extensionShowsConnected: finalAlreadyConnected,
                  backendIsDisconnected: matchedProfileAccount
                    ? Boolean(matchedProfileAccount?.isDisconnected)
                    : undefined,
                  backendHasLinkedinToken: matchedProfileAccount
                    ? Boolean(matchedProfileAccount?.hasLinkedinToken)
                    : undefined,
                  extensionSessionValid,
                  linkedInPublicIdentifier: profilePublicIdentifier,
                  linkedInEntityUrn: profileEntityUrn,
                });
              }
              setIsExtensionLinkedinLoggedOut(false);
              setIsAlreadyConnected(finalAlreadyConnected);
              setConnectedOwnerId(
                finalAlreadyConnected
                  ? String(
                      matchedConnectedAccount?._id ||
                        matchedConnectedAccount?.ownerId ||
                        ""
                    ).trim()
                  : ""
              );
            } else {
              setIsExtensionLinkedinLoggedOut(false);
              setIsAlreadyConnected(false);
              setConnectedOwnerId("");
            }
          } catch (auditErr) {
            // If backend list fails, avoid stale local state for "connected" status.
            postWebConnectionAudit({
              event: "web_sidebar_get_owners_fail",
              source: "web_connect_sidebar",
              extensionShowsConnected: false,
              error:
                auditErr instanceof Error
                  ? auditErr.message
                  : "fetchUserOwnersWithWorkspaces failed",
              linkedInPublicIdentifier: profilePublicIdentifier,
              linkedInEntityUrn: profileEntityUrn,
              details: { reason: "owners_fetch_failed" },
            });
            setIsExtensionLinkedinLoggedOut(false);
            setIsAlreadyConnected(false);
            setConnectedOwnerId("");
          }

          setLinkedInProfile({
            name: profileName,
            profilePicture,
            country: profileCountry,
            entityUrn: profileEntityUrn,
            publicIdentifier: profilePublicIdentifier,
          });

          if (profileCountry) {
            formik.setFieldValue("country", profileCountry);
          }

          window.clearTimeout(timeoutId);
          setIsExtensionProfileLoading(false);
        }
      );
    } catch {
      // Keep UI usable with existing fallbacks if extension messaging fails.
      window.clearTimeout(timeoutId);
      setIsExtensionProfileLoading(false);
      setIsExtensionLinkedinLoggedOut(true);
    }
  }, [
    extensionId,
    isOpen,
    mode,
    reconnectOwnerId,
    resolveCountryFromExtensionProfile,
    formik.setFieldValue,
  ]);

  const handleAddOwnProxy = () => {
    setIsProxyVerified(false);
    setShowOwnProxy(true);
  };

  const handleSwitchBackProxies = () => {
    setShowOwnProxy(false);
  };

  const linkedInName =
    (linkedInProfile.name || formik.values.email.split("@")[0] || "")
      .trim()
      .replace(/[._-]+/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ") || "LinkedIn account";

  const resolveLocalScope = React.useCallback(() => {
    try {
      const tryParse = (raw: string | null) => {
        if (!raw) return null;
        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      };
      const ownerRaw = localStorage.getItem("owner");
      const workspaceRaw = localStorage.getItem("currentWorkSpace");
      const userRaw =
        localStorage.getItem("user") ||
        localStorage.getItem("userData") ||
        sessionStorage.getItem("user") ||
        sessionStorage.getItem("userData");
      const owner = tryParse(ownerRaw);
      const workspace = tryParse(workspaceRaw);
      const user = tryParse(userRaw);
      const ownerId = String(
        reconnectOwnerId ||
          owner?._id ||
          owner?.owner?._id ||
          owner?.data?._id ||
          localStorage.getItem("ownerId") ||
          sessionStorage.getItem("ownerId") ||
          ""
      ).trim();
      const userId = String(
        user?._id ||
          user?.id ||
          user?.userId ||
          owner?.userId?._id ||
          owner?.userId ||
          owner?.user?._id ||
          localStorage.getItem("userId") ||
          sessionStorage.getItem("userId") ||
          ""
      ).trim();
      const workspaceId = String(
        workspace?._id ||
          owner?.workspaces?.[0]?._id ||
          localStorage.getItem("workspaceId") ||
          sessionStorage.getItem("workspaceId") ||
          ""
      ).trim();
      return { ownerId, userId, workspaceId };
    } catch {
      return { ownerId: "", userId: "", workspaceId: "" };
    }
  }, [reconnectOwnerId]);

  const disconnectCurrentConnectedSender = React.useCallback(async () => {
    if (!connectedOwnerId || isDisconnectingConnected) return;
    const { userId, workspaceId } = resolveLocalScope();
    setIsDisconnectingConnected(true);
    setConnectError("");
    try {
      await disconnectLinkedinSender(connectedOwnerId, userId, workspaceId);
      setIsAlreadyConnected(false);
      onDisconnected?.(connectedOwnerId);
      setConnectedOwnerId("");
      setShowConfirmStep(true);
      setShowOwnProxy(false);
      try {
        localStorage.removeItem("owner");
      } catch {
        // ignore localStorage errors
      }
    } catch (error: any) {
      setConnectError(
        error?.response?.data?.msg ||
          error?.message ||
          "Unable to disconnect this account."
      );
    } finally {
      setIsDisconnectingConnected(false);
    }
  }, [
    connectedOwnerId,
    isDisconnectingConnected,
    onDisconnected,
    resolveLocalScope,
  ]);

  const handleVerifyProxy = React.useCallback(async () => {
    const { userId, workspaceId } = resolveLocalScope();
    const reconnectScopedOwnerId = String(reconnectOwnerId || "").trim();
    const proxyHost = String(formik.values.proxyHost || "").trim();
    const proxyPort = String(formik.values.proxyPort || "").trim();
    const proxyUsername = String(formik.values.proxyUsername || "").trim();
    const proxyPassword = String(formik.values.proxyPassword || "");
    const proxyServer =
      proxyHost.startsWith("http://") || proxyHost.startsWith("https://")
        ? proxyHost
        : `http://${proxyHost}`;

    if (!proxyHost || !proxyPort) {
      setConnectError("Proxy host and port are required.");
      setIsProxyVerified(false);
      return;
    }

    setIsVerifyingProxy(true);
    setConnectError("");
    try {
      const response = await axios.post("/owner/testproxy", {
        ownerId: reconnectScopedOwnerId || undefined,
        host: proxyHost,
        server: proxyServer,
        port: proxyPort,
        username: proxyUsername || undefined,
        password: proxyPassword || undefined,
        timeoutMs: 10000,
        enabled: true,
        country: formik.values.country || undefined,
        sessionKey: `manual-${Date.now()}`,
        userId: userId || undefined,
        workspaceId: workspaceId || undefined,
      });
      const payload = response?.data || {};
      const isWorking = payload?.success === true && payload?.working === true;
      if (!isWorking) {
        setIsProxyVerified(false);
        setConnectError(
          payload?.msg ||
            payload?.error ||
            (response?.status === 400
              ? "Invalid proxy details."
              : "Proxy is not working.")
        );
        return;
      }
      const successMsg = payload?.msg || "Proxy verified successfully.";
      setIsProxyVerified(true);
      toast.success(successMsg);
    } catch (error: any) {
      setIsProxyVerified(false);
      setConnectError(extractApiErrorMessage(error, "Unable to verify proxy."));
    } finally {
      setIsVerifyingProxy(false);
    }
  }, [
    formik.values.country,
    formik.values.proxyHost,
    formik.values.proxyPassword,
    formik.values.proxyPort,
    formik.values.proxyUsername,
    reconnectOwnerId,
    resolveLocalScope,
  ]);

  React.useEffect(() => {
    setIsProxyVerified(false);
  }, [
    formik.values.proxyHost,
    formik.values.proxyPort,
    formik.values.proxyUsername,
    formik.values.proxyPassword,
  ]);

  const connectWithManualCredentials = React.useCallback(() => {
    if (isManualConnectLocked(isConnecting, manualStageRef.current)) {
      return;
    }
    const { ownerId, userId, workspaceId } = resolveLocalScope();
    const reconnectScopedOwnerId = String(reconnectOwnerId || "").trim();
    manualConnectRequestedRef.current = true;
    manualConnectCompletedRef.current = false;
    manualFinalizingRef.current = false;
    manualTerminalConnectErrorRef.current = false;
    manualPinSubmitInFlightRef.current = false;
    manualPinSubmittedAtRef.current = 0;
    manualChallengeKindRef.current = null;
    manualBaselineOwnerCountRef.current = -1;
    if (manualConnectTimeoutRef.current) {
      window.clearTimeout(manualConnectTimeoutRef.current);
      manualConnectTimeoutRef.current = null;
    }
    if (!userId && !ownerId) {
      setConnectError("Missing user context. Please refresh and log in again.");
      setIsConnecting(false);
      setManualStage("idle");
      return;
    }

    if (manualSocketRef.current) {
      manualSocketRef.current.disconnect();
      manualSocketRef.current = null;
    }

    const socket = io(SOCKET_URL, {
      ...SOCKET_IO_CLIENT_OPTIONS,
      // Do not auto-reconnect after API restart — that replayed connectLinkedin_emailPassword.
      reconnection: false,
    });
    manualSocketRef.current = socket;
    setManualStage("connecting");
    setIsConnecting(true);
    setConnectError("");

    // Capture baseline owner count before connect for reconciliation fallback.
    fetchUserOwnersWithWorkspaces(1, 100, "")
      .then((res) => {
        const accounts = Array.isArray(res?.accounts) ? res.accounts : [];
        manualBaselineOwnerCountRef.current = accounts.length;
      })
      .catch(() => {
        manualBaselineOwnerCountRef.current = -1;
      });

    const scheduleManualConnectTimeout = () => {
      if (manualConnectTimeoutRef.current) {
        window.clearTimeout(manualConnectTimeoutRef.current);
        manualConnectTimeoutRef.current = null;
      }
      manualConnectTimeoutRef.current = window.setTimeout(async () => {
        if (manualConnectCompletedRef.current || manualFinalizingRef.current) return;
        const stage = manualStageRef.current;
        const waitingForManualChallenge =
          stage === "pin_required" ||
          stage === "microsoft_pin" ||
          stage === "microsoft_signin" ||
          stage === "microsoft_password" ||
          stage === "app_challenge" ||
          stage === "verifying_session" ||
          stage === "connecting";
        if (waitingForManualChallenge) {
          scheduleManualConnectTimeout();
          return;
        }
        const reconciled = await reconcileOwnerCreationFallback();
        if (reconciled) return;
        const timedOutStage = manualStageRef.current;
        const timedOutChallengeKind = manualChallengeKindRef.current;
        setIsConnecting(false);
        setManualStage("idle");
        manualChallengeKindRef.current = null;
        if (timedOutStage === "microsoft_pin") {
          setConnectError(
            "Microsoft sign-in timed out before your verification code was accepted. The browser session has ended. Click Connect Account to try again with a fresh code from your inbox (check spam), or use Connect with Chrome extension."
          );
        } else if (
          timedOutStage === "microsoft_signin" ||
          timedOutChallengeKind === "microsoft"
        ) {
          setConnectError(
            "Microsoft sign-in did not finish in time, and we did not reach the verification step — so a code may not have been sent. Wait a few minutes, then click Connect Account to try again. If this keeps happening, use Connect with Chrome extension."
          );
        } else if (timedOutStage === "pin_required") {
          setConnectError(
            "LinkedIn sign-in timed out before your verification code was accepted. Click Connect Account again and enter a fresh code as soon as it arrives."
          );
        } else {
          setConnectError(
            "Connection is taking longer than expected. If verification is still in progress, wait for a code and submit it — otherwise click Connect Account once more."
          );
        }
      }, MANUAL_CONNECT_TIMEOUT_MS);
    };

    const reconcileOwnerCreationFallback = async () => {
      try {
        const baseline = manualBaselineOwnerCountRef.current;
        if (baseline < 0) return false;
        const res = await fetchUserOwnersWithWorkspaces(1, 100, "");
        const accounts = Array.isArray(res?.accounts) ? res.accounts : [];
        if (accounts.length <= baseline) return false;
        manualConnectCompletedRef.current = true;
        manualFinalizingRef.current = false;
        setConnectError("");
        setIsConnecting(false);
        setManualStage("idle");
        if (manualSocketRef.current) {
          manualSocketRef.current.disconnect();
          manualSocketRef.current = null;
        }
        onClose();
        try {
          if (onConnected) {
            const latestOwner = accounts[0];
            if (latestOwner) onConnected(latestOwner);
          }
        } catch (callbackError) {
          console.error(
            "[ConnectLinkedinCredentialsSidebar] fallback onConnected callback failed",
            callbackError
          );
        }
        return true;
      } catch {
        return false;
      }
    };

    scheduleManualConnectTimeout();

    const applyConnectStage = (
      stage:
        | "idle"
        | "connecting"
        | "pin_required"
        | "microsoft_pin"
        | "microsoft_signin"
        | "microsoft_password"
        | "app_challenge"
        | "verifying_session",
      options?: { error?: string; challengeKind?: string | null }
    ) => {
      manualStageRef.current = stage;
      if (options && "challengeKind" in options) {
        manualChallengeKindRef.current = options.challengeKind ?? null;
      }
      setManualStage(stage);
      setIsConnecting(stage !== "idle");
      if (options && "error" in options) {
        setConnectError(options.error ?? "");
      }
    };

    socket.on("connect_error", (err: unknown) => {
      if (manualSocketRef.current !== socket) return;
      if (manualConnectCompletedRef.current) return;
      if (manualFinalizingRef.current) return;
      manualConnectRequestedRef.current = false;
      setIsConnecting(false);
      setManualStage("idle");
      setConnectError(
        toUserFriendlyConnectMessage(
          formatManualSocketConnectError(err, SOCKET_URL)
        )
      );
    });

    socket.on("disconnect", (reason: string) => {
      if (manualSocketRef.current !== socket) return;
      if (manualConnectCompletedRef.current) return;
      if (manualFinalizingRef.current) return;
      if (reason === "io client disconnect") return;
      const stage = manualStageRef.current;
      const inFlight =
        stage === "connecting" ||
        stage === "verifying_session" ||
        stage === "pin_required" ||
        stage === "microsoft_pin" ||
        stage === "microsoft_signin" ||
        stage === "microsoft_password" ||
        stage === "app_challenge";
      if (!inFlight) return;
      setIsConnecting(false);
      setManualStage("idle");
      if (stage === "app_challenge") {
        setConnectError(
          "The connection to the server dropped while waiting for LinkedIn app approval. Your approval may still have worked — refresh Accounts, or click Connect Account once more. If it keeps failing, use Connect with Chrome extension."
        );
        return;
      }
      setConnectError(
        toUserFriendlyConnectMessage(formatManualSocketDisconnectReason(reason))
      );
    });

    socket.on("connectLinkedin_error", (payload: { error?: string; message?: string; code?: string }) => {
      if (manualSocketRef.current !== socket) return;
      if (manualConnectCompletedRef.current) return;
      if (manualFinalizingRef.current) return;
      if (manualTerminalConnectErrorRef.current) return;
      manualConnectRequestedRef.current = false;
      const nextError = payload?.message || payload?.error || "LinkedIn login failed.";
      const normalizedError = String(nextError || "").toLowerCase();
      const errorCode = String(payload?.code || "").trim();
      const isTerminalConnectError =
        errorCode === "LINKEDIN_ACCOUNT_RESTRICTED" ||
        errorCode === "VERIFICATION_TIMEOUT" ||
        errorCode === "SECURITY_CHALLENGE" ||
        errorCode === "RECONNECT_RETRY" ||
        errorCode === "MICROSOFT_OAUTH_FAILED" ||
        normalizedError.includes("timed out before your email verification") ||
        normalizedError.includes("unable to reach linkedin with this account");
      const currentStage = manualStageRef.current;
      const challengeKind = manualChallengeKindRef.current;
      const inManualChallengeFlow =
        currentStage === "pin_required" ||
        currentStage === "microsoft_pin" ||
        currentStage === "verifying_session" ||
        challengeKind === "linkedin_email" ||
        challengeKind === "microsoft";
      const isRecoverableChallengeError =
        normalizedError.includes("verification code") ||
        normalizedError.includes("sign-in button was not found") ||
        normalizedError.includes("verification submit button") ||
        normalizedError.includes("still waiting for your email verification code") ||
        normalizedError.includes("could not submit the verification code");
      if (!isTerminalConnectError && inManualChallengeFlow && isRecoverableChallengeError) {
        manualPinSubmitInFlightRef.current = false;
        scheduleManualConnectTimeout();
        applyConnectStage(
          challengeKind === "microsoft" || currentStage === "microsoft_pin"
            ? "microsoft_pin"
            : "pin_required",
          {
            challengeKind:
              challengeKind === "microsoft" ? "microsoft" : "linkedin_email",
            error: toUserFriendlyConnectMessage(nextError),
          }
        );
        return;
      }
      const isInvalidCredentialsError =
        normalizedError.includes("wrong email or password") ||
        normalizedError.includes("incorrect linkedin email or password") ||
        normalizedError.includes("incorrect email or password") ||
        normalizedError.includes("invalid email or password");

      manualPinSubmitInFlightRef.current = false;
      manualChallengeKindRef.current = null;
      manualStageRef.current = "idle";
      if (isTerminalConnectError) {
        manualTerminalConnectErrorRef.current = true;
      }
      if (manualConnectTimeoutRef.current) {
        window.clearTimeout(manualConnectTimeoutRef.current);
        manualConnectTimeoutRef.current = null;
      }
      setIsConnecting(false);
      setManualStage("idle");
      setManualPin("");
      if (isInvalidCredentialsError) {
        setShowConfirmStep(false);
      }
      setConnectError(toUserFriendlyConnectMessage(nextError));
    });

    socket.on("connectLinkedin_updateStage", (payload: { stage?: string; message?: string }) => {
      if (manualSocketRef.current !== socket) return;
      if (manualConnectCompletedRef.current) return;
      if (manualFinalizingRef.current) return;
      scheduleManualConnectTimeout();
      const currentStage = manualStageRef.current;
      const challengeKind = manualChallengeKindRef.current;

      if (payload?.stage === "waiting_signin") {
        if (
          challengeKind ||
          currentStage === "pin_required" ||
          currentStage === "microsoft_pin" ||
          currentStage === "microsoft_signin" ||
          currentStage === "microsoft_password" ||
          currentStage === "app_challenge"
        ) {
          return;
        }
        setShowConfirmStep(false);
        applyConnectStage("connecting", {
          error: "Completing sign-in with LinkedIn…",
        });
        return;
      }
      if (payload?.stage === "challenge_microsoft_pending") {
        setShowConfirmStep(false);
        applyConnectStage("microsoft_signin", {
          challengeKind: "microsoft",
          error:
            "LinkedIn is completing an extra sign-in check through Microsoft. Your LinkedIn password is only used on LinkedIn.com — we never send it to Microsoft. If a code or Microsoft password is needed, a field will appear below.",
        });
        return;
      }
      if (payload?.stage === "challenge_microsoft_password") {
        setShowConfirmStep(false);
        manualPinSubmitInFlightRef.current = false;
        applyConnectStage("microsoft_password", {
          challengeKind: "microsoft",
          error: String(payload?.message || "").trim() || getMicrosoftPasswordHint(),
        });
        return;
      }
      if (payload?.stage === "challenge_microsoft") {
        setShowConfirmStep(false);
        manualPinSubmitInFlightRef.current = false;
        applyConnectStage("microsoft_pin", {
          challengeKind: "microsoft",
          error:
            String(payload?.message || "").trim() ||
            "Microsoft is asking for a one-time sign-in code. Check the inbox for this account (and spam), then enter the newest code below.",
        });
        return;
      }
      if (payload?.stage === "challenge_emailVerification") {
        const pinVerifyInProgress =
          currentStage === "verifying_session" ||
          manualPinSubmitInFlightRef.current ||
          (manualPinSubmittedAtRef.current > 0 &&
            Date.now() - manualPinSubmittedAtRef.current < 120000);
        if (pinVerifyInProgress) {
          applyConnectStage("verifying_session", {
            challengeKind: "linkedin_email",
            error: "Verifying LinkedIn session...",
          });
          return;
        }
        setShowConfirmStep(false);
        applyConnectStage("pin_required", {
          challengeKind: "linkedin_email",
          error:
            String(payload?.message || "").trim() ||
            "LinkedIn sent a verification code to your email. Enter it below, then click Submit Code.",
        });
        return;
      }
      if (payload?.stage === "challenge_linkedinApp") {
        if (
          challengeKind === "linkedin_email" ||
          currentStage === "pin_required" ||
          (currentStage === "verifying_session" &&
            challengeKind === "linkedin_email")
        ) {
          return;
        }
        setShowConfirmStep(false);
        applyConnectStage("app_challenge", {
          challengeKind: "linkedin_app",
          error:
            String(payload?.message || "").trim() ||
            "LinkedIn sent a sign-in notification to your LinkedIn app. Open the app, tap Yes it's me, then wait here — do not click Connect again.",
        });
        return;
      }
      if (payload?.stage === "verifying_session") {
        setShowConfirmStep(false);
        applyConnectStage("verifying_session", {
          error: "Verifying LinkedIn session...",
        });
        return;
      }
      if (payload?.stage === "auth_invalid_credentials") {
        manualChallengeKindRef.current = null;
        setShowConfirmStep(false);
        applyConnectStage("idle", {
          challengeKind: null,
          error:
            "Incorrect LinkedIn email or password. Please correct credentials and click Connect Account again.",
        });
      }
      if (payload?.stage === "connect_failed") {
        manualPinSubmitInFlightRef.current = false;
        manualChallengeKindRef.current = null;
        manualStageRef.current = "idle";
        if (manualConnectTimeoutRef.current) {
          window.clearTimeout(manualConnectTimeoutRef.current);
          manualConnectTimeoutRef.current = null;
        }
        setManualPin("");
        setIsConnecting(false);
        setManualStage("idle");
      }
    });

    socket.on(
      "submitMicrosoftPasswordResult",
      (payload: { success?: boolean; error?: string; nextStage?: string }) => {
        if (manualSocketRef.current !== socket) return;
        if (manualConnectCompletedRef.current) return;
        manualPinSubmitInFlightRef.current = false;
        scheduleManualConnectTimeout();
        const nextStage = String(payload?.nextStage || "").trim();
        if (nextStage === "microsoft_pin") {
          applyConnectStage("microsoft_pin", {
            challengeKind: "microsoft",
            error:
              "Microsoft sent a verification code to your email. Enter the newest code below, then click Submit Code.",
          });
          return;
        }
        if (payload?.success && nextStage === "verifying_session") {
          applyConnectStage("verifying_session", {
            error: "Verifying LinkedIn session...",
          });
          return;
        }
        if (nextStage === "microsoft_password" || !payload?.success) {
          applyConnectStage("microsoft_password", {
            challengeKind: "microsoft",
            error: toUserFriendlyConnectMessage(
              payload?.error || "That Microsoft password was not accepted."
            ),
          });
          return;
        }
        if (payload?.success) {
          applyConnectStage("verifying_session", {
            error: "Verifying LinkedIn session...",
          });
        }
      }
    );

    socket.on(
      "typeAndSubmitResult",
      (payload: { success?: boolean; error?: string; nextStage?: string }) => {
        if (manualSocketRef.current !== socket) return;
        if (manualConnectCompletedRef.current) return;
        manualPinSubmitInFlightRef.current = false;
        scheduleManualConnectTimeout();
        const nextStage = String(payload?.nextStage || "").trim();
        if (nextStage === "microsoft_password") {
          applyConnectStage("microsoft_password", {
            challengeKind: "microsoft",
            error: toUserFriendlyConnectMessage(
              payload?.error || "Microsoft is asking for your account password."
            ),
          });
          return;
        }
        if (payload?.success && (nextStage === "verifying_session" || !nextStage)) {
          applyConnectStage("verifying_session", {
            error: "Verifying LinkedIn session...",
          });
          return;
        }
        const pinVerifyInProgress =
          manualStageRef.current === "verifying_session" ||
          (manualPinSubmittedAtRef.current > 0 &&
            Date.now() - manualPinSubmittedAtRef.current < 120000);
        const normalizedPinError = String(payload?.error || "").toLowerCase();
        const isTransientPinWait =
          normalizedPinError.includes("still waiting for your email verification code") ||
          normalizedPinError.includes("verification submit button was not found") ||
          normalizedPinError.includes("could not submit the verification code");
        if (pinVerifyInProgress && isTransientPinWait) {
          applyConnectStage("verifying_session", {
            challengeKind: "linkedin_email",
            error: "Verifying LinkedIn session...",
          });
          return;
        }
        const wasMicrosoftPin =
          manualChallengeKindRef.current === "microsoft" ||
          manualStageRef.current === "microsoft_pin";
        applyConnectStage(wasMicrosoftPin ? "microsoft_pin" : "pin_required", {
          challengeKind: wasMicrosoftPin ? "microsoft" : "linkedin_email",
          error: toUserFriendlyConnectMessage(
            payload?.error ||
              (wasMicrosoftPin
                ? "Could not submit the Microsoft verification code."
                : "Could not submit the LinkedIn verification code.")
          ),
        });
      }
    );

    socket.on(
      "connectLinkedin_success",
      async (payload: {
        data?: {
          name?: string;
          picture?: string;
          publicIdentifier?: string;
          ownerEntityUrn?: string;
          userAgent?: string;
          linkedinBrowserStorageState?: Record<string, unknown>;
          tokens?: {
            linkedin?: {
              li_at?: string;
              session_token?: string;
              li_a?: string;
              cookie_header?: string;
            };
          };
        };
      }) => {
        try {
          if (manualSocketRef.current !== socket) return;
          if (manualConnectCompletedRef.current) return;
          manualFinalizingRef.current = true;
          if (manualConnectTimeoutRef.current) {
            window.clearTimeout(manualConnectTimeoutRef.current);
            manualConnectTimeoutRef.current = null;
          }
          socket.disconnect();
          manualSocketRef.current = null;
          const profile = payload?.data || {};
          const liAt = profile?.tokens?.linkedin?.li_at || "";
          const sessionToken = profile?.tokens?.linkedin?.session_token || "";
          const liA = profile?.tokens?.linkedin?.li_a || "";
          const cookieHeader = profile?.tokens?.linkedin?.cookie_header || "";
          const entityUrn = String(profile?.ownerEntityUrn || "").trim();
          if (!entityUrn || !liAt || !sessionToken) {
            throw new Error("LinkedIn session data is incomplete.");
          }

          const proxyHost = String(formik.values.proxyHost || "").trim();
          const proxyPort = String(formik.values.proxyPort || "").trim();
          const proxyUsername = String(formik.values.proxyUsername || "").trim();
          const proxyPassword = String(formik.values.proxyPassword || "");
          const proxy =
            proxyHost && proxyPort
              ? {
                 
                  
                  host : proxyHost,
                  port: proxyPort,
                  username: proxyUsername || undefined,
                  password: proxyPassword || undefined,
                  
                }
              : undefined;
              

          const loginRes = await axios.post("/owner/login", {
            name: profile?.name || linkedInName,
            ownerEntityUrn: entityUrn,
            picture: profile?.picture || "",
            session_token: sessionToken,
            li_at: liAt,
            li_a: liA,
            cookie_header: cookieHeader,
            email: formik.values.email,
            userAgent: profile?.userAgent || window.navigator.userAgent,
            linkedinBrowserStorageState: profile?.linkedinBrowserStorageState,
            publicIdentifier: profile?.publicIdentifier || "",
            inboxPrivacyConfig: formik.values.inboxPrivacy,
            country: formik.values.country,
            userId: userId || undefined,
            workspaceId: workspaceId || undefined,
            ownerId: reconnectScopedOwnerId || undefined,
            proxyConfig: proxy,
          });
          const loginData = loginRes?.data || {};
          if (!loginData?.status || !loginData?.owner) {
            throw new Error(loginData?.msg || "Unable to link LinkedIn account.");
          }

          // Sync extension context only — do not call extension userLogin here; it posts minimal
          // Chrome cookies to /owner/login and overwrites the Playwright session from credentials.
          let finalOwner = loginData.owner;
          try {
            const resolvedExtensionId = resolveExtensionId();
            const browserChrome = (window as Window & { chrome?: any }).chrome;
            const chromeRuntime = browserChrome?.runtime;
            if (resolvedExtensionId && chromeRuntime?.sendMessage) {
              const ownerRaw = localStorage.getItem("owner");
              const workspaceRaw = localStorage.getItem("currentWorkSpace");
              let ownerContext: any = null;
              let workspaceContext: any = null;
              try {
                ownerContext = ownerRaw ? JSON.parse(ownerRaw) : null;
                workspaceContext = workspaceRaw ? JSON.parse(workspaceRaw) : null;
              } catch {
                ownerContext = null;
                workspaceContext = null;
              }
              const reconnectOwnerContext =
                reconnectScopedOwnerId && ownerContext
                  ? {
                      ...ownerContext,
                      _id: reconnectScopedOwnerId,
                      owner: {
                        ...(ownerContext?.owner || {}),
                        _id: reconnectScopedOwnerId,
                      },
                    }
                  : ownerContext;

              await new Promise<void>((resolve) => {
                chromeRuntime.sendMessage(
                  resolvedExtensionId,
                  {
                    cmd: "syncOwnerContext",
                    data: {
                      owner: reconnectOwnerContext || loginData.owner,
                      workspace: workspaceContext,
                    },
                  },
                  () => resolve()
                );
              });
            }
          } catch (hybridErr) {
            console.warn(
              "[ConnectLinkedinCredentialsSidebar] extension context sync skipped:",
              hybridErr
            );
          }

          manualConnectCompletedRef.current = true;
          manualConnectRequestedRef.current = false;
          setConnectError("");
          setIsConnecting(false);
          setManualStage("idle");
          try {
            if (onConnected) {
              onConnected(finalOwner);
            }
          } catch (callbackError) {
            // Keep sidebar close deterministic even if parent refresh callback fails.
            console.error("[ConnectLinkedinCredentialsSidebar] onConnected callback failed", callbackError);
          }
          onClose();
        } catch (error: any) {
          manualFinalizingRef.current = false;
          setIsConnecting(false);
          setManualStage("idle");
          setConnectError(
            toUserFriendlyConnectMessage(
              extractApiErrorMessage(error, "Unable to connect this LinkedIn account.")
            )
          );
        }
      }
    );

    const emitCredentialsConnect = () => {
      if (!manualConnectRequestedRef.current) return;
      if (manualSocketRef.current !== socket) return;
      const proxyHost = String(formik.values.proxyHost || "").trim();
      const proxyPort = String(formik.values.proxyPort || "").trim();
      const proxyUsername = String(formik.values.proxyUsername || "").trim();
      const proxyPassword = String(formik.values.proxyPassword || "");
      socket.emit("connectLinkedin_emailPassword", {
        email: formik.values.email,
        password: formik.values.password,
        ownerId: reconnectScopedOwnerId || undefined,
        country: formik.values.country,
        ...(proxyHost && proxyPort
          ? {
              proxyConfig: {
                host: proxyHost,
                port: proxyPort,
                username: proxyUsername || undefined,
                password: proxyPassword || undefined,
              },
            }
          : {}),
      });
    };

    if (socket.connected) {
      emitCredentialsConnect();
    } else {
      socket.once("connect", emitCredentialsConnect);
    }
  }, [
    resolveLocalScope,
    reconnectOwnerId,
    linkedInName,
    formik.values.email,
    formik.values.password,
    formik.values.inboxPrivacy,
    formik.values.country,
    onConnected,
    onClose,
  ]);

  const submitManualPin = React.useCallback(() => {
    if (manualPinSubmitInFlightRef.current) return;
    const pin = String(manualPin || "").trim();
    const socket = manualSocketRef.current;
    const reconnectScopedOwnerId = String(reconnectOwnerId || "").trim();
    const wasMicrosoftPin =
      manualChallengeKindRef.current === "microsoft" ||
      manualStageRef.current === "microsoft_pin";
    if (!pin) {
      setConnectError("Enter a valid verification code.");
      return;
    }
    if (!socket) {
      setConnectError(
        "Connect session is not active. Click Connect Account to start again, then enter the code when it arrives."
      );
      return;
    }
    if (!socket.connected) {
      setConnectError(
        "Connection to the server was lost. Click Connect Account to try again, then enter the latest verification code."
      );
      setIsConnecting(false);
      setManualStage(wasMicrosoftPin ? "microsoft_pin" : "pin_required");
      manualStageRef.current = wasMicrosoftPin ? "microsoft_pin" : "pin_required";
      return;
    }
    manualPinSubmitInFlightRef.current = true;
    manualPinSubmittedAtRef.current = Date.now();
    manualStageRef.current = "verifying_session";
    setIsConnecting(true);
    setManualStage("verifying_session");
    setConnectError("Verifying LinkedIn session...");
    socket.emit("typeAndSubmit", {
      action: "typeAndSubmit",
      id: "input__email_verification_pin",
      pin,
      ownerId: reconnectScopedOwnerId || undefined,
    });
  }, [manualPin, reconnectOwnerId]);

  const submitManualMicrosoftPassword = React.useCallback(() => {
    const password = String(manualMicrosoftPassword || "").trim();
    const socket = manualSocketRef.current;
    const reconnectScopedOwnerId = String(reconnectOwnerId || "").trim();
    if (!password || !socket) {
      setConnectError("Enter your Microsoft account password for this email.");
      return;
    }
    manualStageRef.current = "verifying_session";
    setIsConnecting(true);
    setManualStage("verifying_session");
    setConnectError("Submitting Microsoft password…");
    socket.emit("submitMicrosoftPassword", {
      password,
      ownerId: reconnectScopedOwnerId || undefined,
    });
  }, [manualMicrosoftPassword, reconnectOwnerId]);

  const confirmCaptchaCompleted = React.useCallback(() => {
    const socket = manualSocketRef.current;
    if (!socket) {
      setConnectError("Session expired. Please click Connect Account and try again.");
      return;
    }
    setIsConnecting(true);
    setManualStage("verifying_session");
    setConnectError("Verifying LinkedIn session...");
    socket.emit("connectLinkedin_humanDone", {
      ownerId: String(reconnectOwnerId || "").trim() || undefined,
    });
  }, [reconnectOwnerId]);

  const handleConfirmConnect = React.useCallback(async () => {
    if (isManualConnectLocked(isConnecting, manualStageRef.current)) return;
    if (!formik.values.inboxPrivacy || !formik.values.country) {
      setConnectError("Please select inbox privacy and country.");
      return;
    }
    if (mode === "manual") {
      if (!formik.values.email || !formik.values.password) {
        setConnectError("Please provide LinkedIn email and password.");
        return;
      }
      connectWithManualCredentials();
      return;
    }
    if (mode !== "extension" || !extensionId) return;
    const reconnectScopedOwnerId = String(reconnectOwnerId || "").trim();

    const browserChrome = (window as Window & { chrome?: any }).chrome;
    const chromeRuntime = browserChrome?.runtime;
    if (!chromeRuntime?.sendMessage) {
      setConnectError("Chrome extension runtime is unavailable.");
      return;
    }

    setIsConnecting(true);
    setConnectError("");

    try {
      const proxyHost = String(formik.values.proxyHost || "").trim();
      const proxyPort = String(formik.values.proxyPort || "").trim();
      const proxyUsername = String(formik.values.proxyUsername || "").trim();
      const proxyPassword = String(formik.values.proxyPassword || "");
      let extensionProxyPayload:
        | {
            enabled: boolean;
            server: string;
            host: string;
            port: string;
            username?: string;
            password?: string;
            country?: string;
            sessionKey: string;
            source: "client";
          }
        | undefined;

      if (showOwnProxy) {
        if (!proxyHost || !proxyPort) {
          setIsConnecting(false);
          setConnectError("Proxy host and port are required.");
          return;
        }
        if (!isProxyVerified) {
          setIsConnecting(false);
          setConnectError("Verify proxy first to continue.");
          return;
        }

        extensionProxyPayload = {
          enabled: true,
          server: proxyHost,
          host: proxyHost,
          port: proxyPort,
          username: proxyUsername || undefined,
          password: proxyPassword || undefined,
          country: formik.values.country || undefined,
          sessionKey: `extension-${Date.now()}`,
          source: "client",
        };
      }

      chromeRuntime.sendMessage(
        extensionId,
        {
          cmd: "userLogin",
          data: {
            userAgent: window.navigator.userAgent,
            inboxPrivacyConfig: formik.values.inboxPrivacy,
            country: formik.values.country,
            linkedInName: linkedInProfile.name,
            linkedInEntityUrn: "",
            ownerId: reconnectScopedOwnerId || undefined,
            useCustomProxy:true,
            proxyConfig: extensionProxyPayload,
          },
        },
        (res: { status?: boolean; owner?: Record<string, any>; msg?: string; error?: string }) => {
          const chromeLastError = browserChrome?.runtime?.lastError;
          setIsConnecting(false);
          if (chromeLastError || !res?.status) {
            setConnectError(
              toUserFriendlyConnectMessage(
                res?.msg ||
                  res?.error ||
                  chromeLastError?.message ||
                  "Unable to connect this LinkedIn account."
              )
            );
            return;
          }
          onClose();
          try {
            if (res.owner && onConnected) {
              onConnected(res.owner);
            }
          } catch (callbackError) {
            // Keep sidebar close deterministic even if parent refresh callback fails.
            console.error(
              "[ConnectLinkedinCredentialsSidebar] extension onConnected callback failed",
              callbackError
            );
          }
          // Force session/state refresh so owner-dependent guards (e.g. campaign create)
          // pick up newly linked account immediately without manual reload.
          window.location.reload();
        }
      );
    } catch {
      setIsConnecting(false);
      setConnectError(
        toUserFriendlyConnectMessage("Unable to connect this LinkedIn account.")
      );
    }
  }, [
    extensionId,
    formik.values.country,
    formik.values.inboxPrivacy,
    reconnectOwnerId,
    isConnecting,
    isProxyVerified,
    linkedInProfile.name,
    mode,
    onClose,
    onConnected,
    connectWithManualCredentials,
    showOwnProxy,
    formik.values.proxyHost,
    formik.values.proxyPort,
    formik.values.proxyUsername,
    formik.values.proxyPassword,
    formik.values.email,
    formik.values.password,
  ]);

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title="Connect LinkedIn account"
      size="small"
    >
      {isCredentialsDisabled ? (
        <div className="space-y-4 p-4 text-left">
          <p className="text-sm text-dark-grey">
            Connecting LinkedIn via credentials is currently disabled. Please use the Chrome
            extension to connect your account.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="sm"
              type="button"
              fullWidth
              data-bypass-plan-gate="true"
              onClick={() => {
                if (onBack) onBack();
                else onClose();
              }}
            >
              Back
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="button"
              fullWidth
              data-bypass-plan-gate="true"
              onClick={() => {
                if (onBack) onBack();
                else onClose();
              }}
            >
              Use Extension
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={formik.handleSubmit} className="space-y-5 text-left">
          {mode === "extension" ? (
            <MultipleAccountsHelpGuide variant="compact" defaultExpanded={false} />
          ) : null}
          <fieldset
            disabled={isCredentialsFieldsetLocked(isConnecting, manualStage)}
            className="m-0 min-w-0 space-y-5 border-0 p-0"
          >
        {showConfirmStep ? (
          <div className="space-y-4 pb-3">
            {mode === "extension" ? (
              <div className="flex justify-center pt-1">
                <img
                  src="/new-logo/logo.png"
                  alt="Innlead"
                  className="h-8 w-auto object-contain"
                />
              </div>
            ) : null}
            {mode === "extension" && isExtensionProfileLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-light-grey border-t-brand-blue" />
                <p className="mt-4 text-md text-dark-grey">Loading LinkedIn details...</p>
              </div>
            ) : null}

            {mode === "extension" &&
            !isExtensionProfileLoading &&
            isExtensionLinkedinLoggedOut ? (
              <div className="space-y-4 px-1 py-2">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  type="button"
                  data-bypass-plan-gate="true"
                  onClick={() => {
                    if (onBack) onBack();
                    else onClose();
                  }}
                >
                  {"<"} Back
                </Button>

                <p className="text-center text-body-medium text-dark-grey">
                  Connect your LinkedIn account via our Chrome extension
                </p>
              </div>
            ) : null}

            {!(mode === "extension" && isExtensionProfileLoading) ? (
              <>
            {!(mode === "extension" && isExtensionLinkedinLoggedOut) ? (
              <>
            {isAlreadyConnected ? (
              <div className="space-y-4 pb-3 pt-1 text-center">
                <div className="relative mx-auto w-fit">
                  <LeadSenderAvatar
                    picture={linkedInProfile.profilePicture}
                    className="mx-auto h-[88px] w-[88px]"
                  />
                  <span className="absolute right-1 top-1 h-4 w-4 rounded-full border-2 border-white bg-mainColor" />
                </div>
                <h3 className="mt-4 text-md font-semibold text-dark-black">{linkedInName}</h3>
                <p className="text-md text-dark-grey">is logged into Innlead</p>
                <div className="space-y-3 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    type="button"
                    data-bypass-plan-gate="true"
                    className="bg-light-grey border-light-grey text-dark-grey hover:bg-light-grey"
                    disabled={isDisconnectingConnected}
                    onClick={disconnectCurrentConnectedSender}
                  >
                    {isDisconnectingConnected
                      ? "Disconnecting..."
                      : "Disconnect Account"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    type="button"
                    data-bypass-plan-gate="true"
                    className="bg-white border-dark-black text-dark-black"
                    onClick={() => {
                      window.open(
                        LINKEDIN_LOGOUT_URL,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    Connect Another Account
                  </Button>
                </div>
              </div>
            ) : (
              <>
            {mode === "extension" ? (
              <div className="space-y-4 pb-3 pt-1 text-center">
                <div className="relative mx-auto w-fit">
                  <LeadSenderAvatar
                    picture={linkedInProfile.profilePicture}
                    className="mx-auto h-[88px] w-[88px]"
                  />
                  <span className="absolute right-1 top-1 h-4 w-4 rounded-full border-2 border-white bg-mainColor" />
                </div>
              </div>
            ) : null}

            <div className="pt-1 text-center">
              <h3 className="text-md font-semibold text-dark-black">
                Do you want to connect {linkedInName}
                <br />
                to Innlead?
              </h3>
            </div>

            <div className="text-left">
              <Select
                label="Select which conversations to load into Innlead"
                placeholder="Choose your inbox privacy configuration"
                value={formik.values.inboxPrivacy}
                onChange={(value) => formik.setFieldValue("inboxPrivacy", value)}
                options={[
                  { value: "private", label: "Only my private inbox" },
                  { value: "shared", label: "Shared / team inbox" },
                ]}
              />
            </div>

            <div className="text-left">
              <Select
                label="Select your country"
                placeholder="Choose your country"
                value={formik.values.country}
                onChange={(value) => formik.setFieldValue("country", value)}
                options={countryOptions}
              />
            </div>

            <div className="space-y-3 pt-1">
              {showOwnProxy ? (
                <div className="space-y-4 pt-2 text-left">
                  <div className="flex items-start gap-3 rounded-[8px] border border-proxy-warning-border bg-proxy-warning-bg px-4 py-3">
                    <svg
                      viewBox="0 0 24 24"
                      className="mt-0.5 h-5 w-5 flex-none text-proxy-warning-icon"
                      aria-hidden="true"
                    >
                      <path
                        fill="currentColor"
                        d="M12 2.75c.43 0 .82.23 1.03.6l9.1 15.74c.22.38.22.86 0 1.24c-.21.37-.6.6-1.03.6H1.9c-.43 0-.82-.23-1.03-.6c-.22-.38-.22-.86 0-1.24L10.97 3.35c.21-.37.6-.6 1.03-.6Zm0 6a1 1 0 0 0-1 1v4.5a1 1 0 1 0 2 0V9.75a1 1 0 0 0-1-1Zm0 10.25a1.25 1.25 0 1 0 0-2.5a1.25 1.25 0 0 0 0 2.5Z"
                      />
                    </svg>
                    <p className="text-md text-proxy-warning-text">
                      Adding your own proxy can affect your overall Innlead experience. Proceed only if you
                      know what you are doing.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-md font-semibold text-dark-black">Verify your proxy</h3>

                    <Input
                      id="proxyHost"
                      name="proxyHost"
                      placeholder="Host"
                      value={formik.values.proxyHost}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />

                    <Input
                      id="proxyPort"
                      name="proxyPort"
                      placeholder="Port"
                      value={formik.values.proxyPort}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />

                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        id="proxyUsername"
                        name="proxyUsername"
                        placeholder="Proxy Username"
                        value={formik.values.proxyUsername}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />

                      <Input
                        id="proxyPassword"
                        name="proxyPassword"
                        placeholder="Proxy Password"
                        type="password"
                        value={formik.values.proxyPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />

                      <Button
                        variant="outline"
                        size="sm"
                        fullWidth
                        type="button"
                        isDisabled={
                          isVerifyingProxy ||
                          !formik.values.proxyHost ||
                          !formik.values.proxyPort
                        }
                        data-bypass-plan-gate="true"
                        className="bg-light-grey border-light-grey text-dark-grey hover:bg-light-grey"
                        onClick={handleVerifyProxy}
                      >
                        {isVerifyingProxy ? "Verifying..." : "Verify proxy"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                fullWidth
                type="button"
                data-bypass-plan-gate="true"
                onClick={handleConfirmConnect}
                disabled={
                  isVerifyingProxy ||
                  (showOwnProxy && !isProxyVerified) ||
                  (mode === "manual" &&
                    (isManualConnectLocked(isConnecting, manualStage) ||
                      showManualPinUi(manualStage) ||
                      showMicrosoftPasswordUi(manualStage)))
                }
              >
                {isManualConnectLocked(isConnecting, manualStage)
                  ? getManualProgressMessage(manualStage)
                  : "Yes, Connect"}
              </Button>
              {connectError ? (
                <p
                  role="status"
                  className={
                    isManualConnectNotice(manualStage)
                      ? "rounded-[8px] border border-blue-200 bg-blue-50 px-3 py-2 text-left text-sm font-medium text-blue-900"
                      : "rounded-[8px] border border-red-300 bg-red-50 px-3 py-2 text-left text-sm font-medium text-red-700"
                  }
                >
                  {connectError}
                </p>
              ) : null}

              <Button
                variant="outline"
                size="sm"
                fullWidth
                type="button"
                data-bypass-plan-gate="true"
                className="bg-light-grey border-light-grey text-dark-grey hover:bg-light-grey"
                disabled={isManualConnectLocked(isConnecting, manualStage)}
                onClick={() => {
                  setShowOwnProxy(false);
                  setShowConfirmStep(false);
                }}
              >
                No, Change Account
              </Button>

              <Button
                variant="outline"
                size="sm"
                fullWidth
                type="button"
                data-bypass-plan-gate="true"
                disabled={isManualConnectLocked(isConnecting, manualStage)}
                onClick={() => {
                  setShowOwnProxy((prev) => {
                    const next = !prev;
                    if (next) setIsProxyVerified(false);
                    return next;
                  });
                }}
              >
                {showOwnProxy ? "Switch Back Innlead Proxies" : "Add Your Proxy"}
              </Button>

              <button
                type="button"
                className="w-full pt-1 text-md text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
                data-bypass-plan-gate="true"
                disabled={isManualConnectLocked(isConnecting, manualStage)}
                onClick={() => {
                  setShowConfirmStep(false);
                  setShowOwnProxy(false);
                }}
              >
                Or Login manually with credentials
              </button>
            </div>
              </>
            )}
              </>
            ) : null}
              </>
            ) : null}
          </div>
        ) : (
          <>
        <Input
          label="Your LinkedIn Email address"
          id="email"
          name="email"
          placeholder="LinkedIn email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email ? formik.errors.email : ""}
          containerClassName="text-left"
          disabled={areCredentialInputsLocked(isConnecting, manualStage)}
        />

        <Input
          label="Your LinkedIn password"
          id="password"
          name="password"
          placeholder="Linkedin password"
          type="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password ? formik.errors.password : ""}
          containerClassName="text-left"
          disabled={areCredentialInputsLocked(isConnecting, manualStage)}
        />

        <div className="text-left">
          <Select
            label="Select which conversations load into Innlead"
            placeholder="Choose your inbox privacy configuration"
            value={formik.values.inboxPrivacy}
            onChange={(value) => formik.setFieldValue("inboxPrivacy", value)}
            options={[
              { value: "private", label: "Only my private inbox" },
              { value: "shared", label: "Shared / team inbox" },
            ]}
          />
          {formik.touched.inboxPrivacy && formik.errors.inboxPrivacy ? (
            <p className="mt-2 rounded-[8px] border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {formik.errors.inboxPrivacy}
            </p>
          ) : null}
        </div>

        <div className="text-left">
          <Select
            label="Select your country"
            placeholder="Choose your country"
            value={formik.values.country}
            onChange={(value) => formik.setFieldValue("country", value)}
            options={countryOptions}
          />
          {formik.touched.country && formik.errors.country ? (
            <p className="mt-2 rounded-[8px] border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {formik.errors.country}
            </p>
          ) : null}
        </div>

        {showOwnProxy ? (
          <div className="space-y-4 pt-2 text-left">
            <div className="flex items-start gap-3 rounded-[8px] border border-proxy-warning-border bg-proxy-warning-bg px-4 py-3">
              <svg
                viewBox="0 0 24 24"
                className="mt-0.5 h-5 w-5 flex-none text-proxy-warning-icon"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M12 2.75c.43 0 .82.23 1.03.6l9.1 15.74c.22.38.22.86 0 1.24c-.21.37-.6.6-1.03.6H1.9c-.43 0-.82-.23-1.03-.6c-.22-.38-.22-.86 0-1.24L10.97 3.35c.21-.37.6-.6 1.03-.6Zm0 6a1 1 0 0 0-1 1v4.5a1 1 0 1 0 2 0V9.75a1 1 0 0 0-1-1Zm0 10.25a1.25 1.25 0 1 0 0-2.5a1.25 1.25 0 0 0 0 2.5Z"
                />
              </svg>
              <p className="text-md text-proxy-warning-text">
                Adding your own proxy can affect your overall Innlead experience. Proceed only if you
                know what you are doing.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-semibold text-dark-black">Verify your proxy</h3>

              <Input
                id="proxyHost"
                name="proxyHost"
                placeholder="Host"
                value={formik.values.proxyHost}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <Input
                id="proxyPort"
                name="proxyPort"
                placeholder="Port"
                value={formik.values.proxyPort}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <div className="grid grid-cols-3 gap-4">
                <Input
                  id="proxyUsername"
                  name="proxyUsername"
                  placeholder="Proxy Username"
                  value={formik.values.proxyUsername}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />

                <Input
                  id="proxyPassword"
                  name="proxyPassword"
                  placeholder="Proxy Password"
                  type="password"
                  value={formik.values.proxyPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />

                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  type="button"
                  isDisabled={
                    isVerifyingProxy ||
                    !formik.values.proxyHost ||
                    !formik.values.proxyPort
                  }
                  data-bypass-plan-gate="true"
                  className="bg-light-grey border-light-grey text-dark-grey hover:bg-light-grey"
                  onClick={handleVerifyProxy}
                >
                  {isVerifyingProxy ? "Verifying..." : "Verify proxy"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="pt-4">
          <div className="flex gap-4">
            <Button
              variant="primary"
              size="sm"
              fullWidth
              type="submit"
              data-bypass-plan-gate="true"
              disabled={
                !(formik.isValid && formik.dirty) ||
                (mode === "manual" &&
                  (isManualConnectLocked(isConnecting, manualStage) ||
                    showManualPinUi(manualStage)))
              }
              onClick={() => {
                // clicking should still allow sidebar interactions
              }}
            >
              {isManualConnectLocked(isConnecting, manualStage)
                ? getManualProgressMessage(manualStage)
                : "Connect Account"}
            </Button>

            {showOwnProxy ? (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                className="bg-light-grey border-light-grey text-dark-grey hover:bg-light-grey"
                type="button"
                data-bypass-plan-gate="true"
                disabled={isManualConnectLocked(isConnecting, manualStage)}
                onClick={handleSwitchBackProxies}
              >
                Switch Back Innlead Proxies
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                className="bg-light-grey border-light-grey text-dark-grey hover:bg-light-grey"
                type="button"
                data-bypass-plan-gate="true"
                disabled={isManualConnectLocked(isConnecting, manualStage)}
                onClick={handleAddOwnProxy}
              >
                Add Your Own Proxy
              </Button>
            )}
          </div>
          {connectError ? (
            <p
              role="status"
              className={
                isManualConnectNotice(manualStage)
                  ? "mt-2 rounded-[8px] border border-blue-200 bg-blue-50 px-3 py-2 text-left text-sm font-medium text-blue-900"
                  : "mt-2 rounded-[8px] border border-red-300 bg-red-50 px-3 py-2 text-left text-sm font-medium text-red-700"
              }
            >
              {connectError}
            </p>
          ) : null}
          {!connectError &&
          isManualConnectLocked(isConnecting, manualStage) &&
          !showManualPinUi(manualStage) ? (
            <p className="pt-2 text-center text-sm text-dark-grey">
              {getManualProgressMessage(manualStage)}
            </p>
          ) : null}
        </div>
          </>
        )}
          </fieldset>

        {mode === "manual" && showManualPinUi(manualStage) ? (
          <div
            ref={manualPinSectionRef}
            className="mt-4 rounded-[8px] border border-light-grey bg-white p-3 pb-4"
          >
            <p className="mb-2 text-sm text-dark-grey">{getManualPinHint(manualStage)}</p>
            <Input
              id="manualPin"
              name="manualPin"
              placeholder="Enter verification code"
              value={manualPin}
              onChange={(e) => setManualPin(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitManualPin();
                }
              }}
              containerClassName="text-left"
              autoComplete="one-time-code"
            />
            <Button
              variant="primary"
              size="sm"
              fullWidth
              type="button"
              className="mt-2"
              data-bypass-plan-gate="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                submitManualPin();
              }}
              disabled={
                manualStage === "verifying_session" || !String(manualPin || "").trim()
              }
            >
              {manualStage === "verifying_session" ? "Submitting..." : "Submit Code"}
            </Button>
          </div>
        ) : null}

        {mode === "manual" && showMicrosoftPasswordUi(manualStage) ? (
          <div
            ref={manualMicrosoftPasswordSectionRef}
            className="mt-4 rounded-[8px] border border-light-grey bg-white p-3 pb-4"
          >
            <p className="mb-2 text-sm text-dark-grey">{getMicrosoftPasswordHint()}</p>
            <Input
              id="manualMicrosoftPassword"
              name="manualMicrosoftPassword"
              label="Microsoft account password (only if prompted)"
              placeholder="Microsoft password for this email"
              type="password"
              value={manualMicrosoftPassword}
              onChange={(e) => setManualMicrosoftPassword(e.target.value)}
              containerClassName="text-left"
              autoComplete="current-password"
            />
            <Button
              variant="primary"
              size="sm"
              fullWidth
              type="button"
              className="mt-2"
              data-bypass-plan-gate="true"
              onClick={submitManualMicrosoftPassword}
              disabled={manualStage === "verifying_session"}
            >
              {manualStage === "verifying_session"
                ? "Submitting..."
                : "Submit Microsoft Password"}
            </Button>
          </div>
        ) : null}
        </form>
      )}
    </Sidebar>
  );
}

